import api from "../api";

const normalizeSurveyResponse = (surveyData) => {
    if (typeof surveyData !== "string") {
        return surveyData;
    }

    try {
        return JSON.parse(surveyData);
    } catch {
        return surveyData;
    }
};

export const getAllPatients = async (doctorId) => {
    try {
        const response = await api.get(`/patients`, {
            params: {doctor_id:doctorId},
            credentials: "include",
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const getPatient = async (patientId) => {
    try {
        const response = await api.get(`/patients/${patientId}`, {
            credentials: "include",
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const updatePatient = async (
    patientId,
    firstName,
    lastName,
    email,
    phone
) => {
    try {
        const response = await api.put(
            `/patients/${patientId}`,
            {firstName, lastName, email, phone},
            {
                credentials: "include",
            }
        );

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const createPatient = async (
    doctorId,
    firstName,
    lastName,
    email,
    phone
) => {
    try {
        const response = await api.post(
            "/patients",
            {doctorId, firstName, lastName, email, phone},
            {
                credentials: "include",
            }
        );

        if (!(response.status === 201)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const deletePatient = async (patientId) => {
    try {
        const response = await api.delete(`/patients/${patientId}`, {
            credentials: "include",
        });
        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }
        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const searchPatients = async (doctorId, search) => {
    try {
        const response = await api.get(`/patients/search`, {
            params: {
                doctor_id: doctorId,
                q: search,
            },
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const getReportRequestsByPatient = async (patientId) => {
    try {
        const response = await api.get(`/report_requests`, {
            credentials: "include",
            params: {patient_id: patientId},
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};

export const getReportByPatient = async (patientId) => {
    try {
        const response = await api.get(`/report/${patientId}`, {
            credentials: "include",
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};


export const getPdfReportByPatient = async (patientId) => {
    try {
        const response = await api.get(`/pdf_report/${patientId}`, {
            credentials: "include",
        });

        if (!(response.status === 200)) {
            throw new Error("Network response was not ok");
        }

        return await response.data;
    } catch (error) {
        throw error.message;
    }
};


export const createReportRequest = async (patientId, doctorId) => {
    try {
        const response = await api.post("/report_requests", {
            requested_for: patientId,
            requested_by: doctorId,
        });

        if (response.status !== 201) {
            throw new Error("Failed to create report request");
        }

        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error; // Throw the whole error instead of error.message
    }
};

export const createSurvey = async (patientId, doctorId, surveyData) => {
    try {
        const response = await api.post("/surveys", {
            patient_id: patientId,
            doctor_id: doctorId,
            survey_response: normalizeSurveyResponse(surveyData),
        });

        if (response.status !== 201) {
            throw new Error("Failed to Submit Survey");
        }
        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error;
    }
};

export const createStandaloneSurvey = async (surveyData, patient_id) => {
    try {
        const response = await api.post("/surveys", {
            patient_id,
            survey_response: normalizeSurveyResponse(surveyData),
        });

        if (response.status !== 201) {
            throw new Error("Failed to Submit Survey");
        }
        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error;
    }
};

export const getSurveysByPatientId = async (patientId) => {
    try {
        const response = await api.get(`/surveys/${patientId}`, {
            credentials: "include",
        });

        if (response.status !== 200) {
            throw new Error("Failed to fetch surveys");
        }

        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error;
    }
};

export const getSurveysByPatientandSurveyId = async (patientId, surveyId) => {
    try {
        const response = await api.get(`/surveys/${patientId}/${surveyId}`, {
            credentials: "include",
        });

        if (response.status !== 200) {
            throw new Error("Failed to fetch surveys");
        }

        return response.data;
    } catch (error) {
        console.log("API Error", error);
        throw error;
    }
};

export const getSurveyToken = async (patientId, doctorId) => {
    try {
        const response = await api.post(`/surveys/token/${patientId}`);
        if (!(response.status === 201)) {
            throw new Error("Network response was not ok");
        }
        const responseEmail = await api.post(`/patients/${patientId}/send-survey`, {
            "doctorId": doctorId
        });
        if (!(responseEmail.status === 200)) {
            throw new Error("Network response was not ok");
        }
        return response.data.token || response.data?.token || response.data;
    } catch (error) {
        throw error.message;
    }
};

export const submitSurveyWithToken = async (token, formData) => {
    try {
        const response = await api.post("/surveys/submit", formData, {
            headers: {
                'X-Survey-Token': token,
                'Content-Type': 'multipart/form-data',
            },
        });
        if (response.status !== 201 && response.status !== 200) {
            throw new Error("Failed to submit survey");
        }
        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error;
    }
};

export const getPatientByToken = async (token) => {
    try {
        const response = await api.get("/surveys/patient", {
            headers: {
                'X-Survey-Token': token,
            },
        });
        if (response.status !== 200) {
            throw new Error("Failed to fetch patient");
        }
        return response.data;
    } catch (error) {
        console.error("API error:", error);
        throw error;
    }
}

export const getPatientAdherence = async(patientId,period)=>{
    try{
        const response = await api.get(`me/routines/adherence?patient_id=${patientId}&period=${period}`,{
            credentials: "include",
        });
        if (response.status !== 200) {
            throw new Error("Failed to fetch adherence");
        }
        return response.data;
    }catch(error){
        console.error("API error:", error);
        throw error;
    }
}
